import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { clsHelper } from '../../utilities/class-name-helper';
import { attrNames } from '../../utilities/global-constants';

import { IPdpTabContentData } from './pdp-tab-content.data';
import { IPdpTabContentProps, IPdpTabContentResources } from './pdp-tab-content.props.autogenerated';

//==============================================================================
// INTERFACES AND CONSTANTS
//==============================================================================

type PdpTabSlots = {
    fitsModelTab: React.ReactNode[];
    kitTab: React.ReactNode[];
    kitBenefitsTab: React.ReactNode[];
    specifications: React.ReactNode[];
    description: React.ReactNode[];
};

//==============================================================================
// CLASS NAME UTILITY
//==============================================================================
const BASE_CLASS = 'pdp-tab-content';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);

/**
 *
 * PdpTabContent component
 * @extends {React.Component<IPdpTabContentProps<IPdpTabContentData>>}
 */
@observer
class PdpTabContent extends React.Component<IPdpTabContentProps<IPdpTabContentData>> {

    @observable private activetab: string = 'fits-model';

    //==========================================================================
    // PUBLIC METHODS
    //==========================================================================

    //------------------------------------------------------
    //------------------------------------------------------
    public constructor(props: IPdpTabContentProps<IPdpTabContentData>) {
        super(props);
        this._changeTab = this._changeTab.bind(this);
    }

    //------------------------------------------------------
    // Render function
    //------------------------------------------------------
    public render(): JSX.Element | null {
        const { resources, slots } = this.props;
        const constProductType = attrNames.attributeValueProductType;
        const productType = this.props.data.productSpecificationData.result?.find(product => {
            return product.Name === constProductType;
        });

        let displayContent = null;
        if (productType && productType.TextValue === attrNames.attributeValueKit) {
            //Kit tabs are no longer being used, if they are copy format similar to that of line 76
            //const useTabs = !!((slots.kitBenefitsTab) || (slots.kitTab));
            displayContent = (
                <div className={BASE_CLASS}>
                    {this._renderKitTabs(resources, slots)}
                    {this._renderKitContent(slots)}
                </div>
            );
        }
        if (productType && productType.TextValue === attrNames.attributeValuePart) {
            const useTabs = !!((slots.specifications) || (slots.description));
            displayContent = (
                <div className={BASE_CLASS}>
                    {useTabs && this._renderModelTabs(resources, slots)}
                    {this._renderModelContent(slots)}
                </div>
            );
        }
        return displayContent;
    }

    //------------------------------------------------------
    //------------------------------------------------------
    public _renderKitTabs(resources: IPdpTabContentResources, slotItems: PdpTabSlots): JSX.Element {
        return (
            <div className={cls('tabs')}>
                <a
                    className={classnames(cls('tab'), 'fits-model', { 'active': this._isActive('fits-model') })}
                    onClick={this._changeTab('fits-model')}
                    role='button'
                >
                    {resources.pdpTabFitsModel}
                </a>
                {slotItems.kitTab &&
                    <a
                        className={classnames(cls('tab'), 'kit', { 'active': this._isActive('kit') })}
                        onClick={this._changeTab('kit')}
                        role='button'
                    >
                        {resources.pdpTabKit}
                    </a>
                }
                {slotItems.kitBenefitsTab &&
                    <a
                        className={classnames(cls('tab'), 'kit-benefits', { 'active': this._isActive('kit-benefits') })}
                        onClick={this._changeTab('kit-benefits')}
                        role='button'
                    >
                        {resources.pdpTabKitBenefits}
                    </a>
                }
            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    public _renderKitContent(slotItems: PdpTabSlots): JSX.Element {
        return (
            <div className={cls('container')}>
                {this._isActive('fits-model') && slotItems.fitsModelTab && this._renderSlot(slotItems.fitsModelTab, classnames(cls('content'), 'fits-model'))}
                {this._isActive('kit') && slotItems.kitTab && this._renderSlot(slotItems.kitTab, classnames(cls('content'), 'kit'))}
                {this._isActive('kit-benefits') && slotItems.kitBenefitsTab && this._renderSlot(slotItems.kitBenefitsTab, classnames(cls('content'), 'kit-benefits'))}
            </div>
        );
    }
    //------------------------------------------------------
    //------------------------------------------------------
    public _renderModelTabs(resources: IPdpTabContentResources, slotItems: PdpTabSlots): JSX.Element {
        return (
            <div className={cls('tabs')}>
                <a
                    className={classnames(cls('tab'), 'fits-model', { 'active': this._isActive('fits-model') })}
                    onClick={this._changeTab('fits-model')}
                    role='button'
                >
                    {resources.pdpTabFitsModel}
                </a>
                {slotItems.specifications &&
                    <a
                        className={classnames(cls('tab'), 'specification', { 'active': this._isActive('specification') })}
                        onClick={this._changeTab('specification')}
                        role='button'
                    >
                        {resources.pdpSpecifications}
                    </a>
                }
                {slotItems.description &&
                    <a
                        className={classnames(cls('tab'), 'description', { 'active': this._isActive('description') })}
                        onClick={this._changeTab('description')}
                        role='button'
                    >
                        {resources.pdpDescription}
                    </a>
                }
            </div>
        );
    }
    //------------------------------------------------------
    //------------------------------------------------------
    public _renderModelContent(slotItems: PdpTabSlots): JSX.Element {
        return (
            <div className={cls('container')}>
                {this._isActive('fits-model') && slotItems.fitsModelTab && this._renderSlot(slotItems.fitsModelTab, classnames(cls('content'), 'fits-model'))}
                {this._isActive('specification') && slotItems.specifications && this._renderSlot(slotItems.specifications, classnames(cls('content'), 'specification'))}
                {this._isActive('description') && slotItems.description && this._renderSlot(slotItems.description, classnames(cls('content'), 'description'))}
            </div>
        );
    }
    //------------------------------------------------------
    //------------------------------------------------------
    public _renderSlot(slotItems: React.ReactNode[], slotClass?: string): JSX.Element {
        return (
            <div className={slotClass}>
                {slotItems.map((slot: React.ReactNode, index: number) => (
                    <React.Fragment key={index}>
                        {slot}
                    </React.Fragment>
                ))}
            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    @action
    public _changeTab = (clickedTab: string) => () => {
        this.activetab = clickedTab;
    };

    //------------------------------------------------------
    //------------------------------------------------------
    public _isActive = (tab: string): boolean => {
        return this.activetab === tab;
    };
}

export default PdpTabContent;